<template>
  <div class="overlay"></div>
  <div class="modal-container card-elevated">
    <div class="card-header" :class="{ 'spinner': isLoading }">
      <div class="header-text">Manage external users</div>
      <span class="material-symbols-outlined close" @click="$emit('close-modal')">close</span>
    </div>
    <div class="modal-body">
      <div class="heading">
        <p class="heading-content">Add and remove users from this tumor board.</p>
      </div>
      <div class="add-button-container">
        <button class="search-physician primary-filled-button" @click="isInviteModalVisible = true">
          <i class="material-symbols-outlined">add</i> <span>Add User</span>
        </button>
      </div>
      <!-- TODO: Styles for this DataTable are currently set in PhysicianScheduleAdmin.vue. This is confusing.
       Styles that are used across components should be declared in a centralized location. -->
      <div class="scheduled-physicians-div">
        <DataTable v-if="!externalUsers">
          <template #empty>
            <p class="no-records-class"> This tumor board does not have any external users</p>
          </template>
          <Column field="name" header="Name" sortable></Column>
          <Column field="email" header="Email" sortable></Column>
        </DataTable>
        <DataTable :value="externalUsers" paginator editMode="cell" tableStyle="min-width: 10rem"
          :alwaysShowPaginator=false tableClass="editable-cells-table" :rows=5>
          <Column field="caregiver_name" header="Name" sortable>
            <template #body="{ data }">
              {{ data.name }}
            </template>
          </Column>
          <Column field="caregiver_email" header="Email Id">
            <template #body="{ data }">
              {{ data.email }}
            </template>
          </Column>
          <Column>
            <template #body="rowData">
              <span class="material-symbols-outlined icon-button-to-primary size-20 icon-delete" title="Delete Row"
                @click="handleDelete(rowData.data)">cancel</span>
            </template>
          </Column>
        </DataTable>
      </div>
    </div>
    <InviteExternalUsersForm v-if="isInviteModalVisible" :name="tumorBoardName" :date="tumorBoardDate"
      @close-modal="isInviteModalVisible = false" @send-external-invite="sendExternalInvite" />
  </div>
</template>

<script setup>
import { defineEmits, defineProps, onMounted, ref } from 'vue'
import InviteExternalUsersForm from '@/ux3/components/ExternalUsers/InviteExternalUsersForm.vue'
import { deleteExternalUser, getExternalUsers, putExternalUser } from '@/ux3/apis/externalUser.js'
import { updateCalendarInvite } from '@/ux3/apis/updateCalendarInvite.js'
import { showToast } from '@/utils/utils.js'

defineEmits(['close-modal', 'send-external-invite'])

const props = defineProps({
  tumorBoardName: {
    type: String,
    required: true,
  },
  tumorBoardDate: {
    type: String,
    required: true,
  },
  tumorBoardId: {
    type: String,
    required: true,
  },
})

const isInviteModalVisible = ref(false)
const externalUsers = ref([])
const isLoading = ref(false)

const sendExternalInvite = async (data) => {
  const payload = {
    name: data.name,
    email: data.email,
    tumorboard_id: props.tumorBoardId,
  }
  const resp = await putExternalUser(payload)

  if (resp.status === 200) {
    showToast('success', `${data.name} has been successfully invited to the tumor board.`)
    await updateCalendarInvite(props.tumorBoardId)
  } else {
    showToast('error', `There was a problem inviting ${data.name} to the tumor board`)
  }
  await getAndSaveExternalUsers()
  isInviteModalVisible.value = false
}

const getAndSaveExternalUsers = async () => {
  isLoading.value = true
  const response = await getExternalUsers(props.tumorBoardId)
  externalUsers.value = response.result
  isLoading.value = false
}

const handleDelete = async (data) => {
  isLoading.value = true
  const delResp = await deleteExternalUser(data.external_attendee_id)
  if (delResp.status === 200) {
    showToast('success', `${data.name} has been successfully deleted from the tumor board.`)
    const getResp = await getExternalUsers(props.tumorBoardId)
    externalUsers.value = getResp.result

    await updateCalendarInvite(props.tumorBoardId)
  } else {
    showToast('error', `There was a problem deleting ${data.name} from the tumor board`)
  }
  isLoading.value = false
}

onMounted(async () => {
  await getAndSaveExternalUsers()
})
</script>

<style lang="scss" scoped>
.heading-content {
  color: var(--Grey600);
  font-size: 0.9rem;
  font-style: normal;
  font-weight: 300;
}

// TODO: remove unused classes
.overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: transparent;
  z-index: 999;
}

.modal-container {
  position: absolute;
  top: 5%;
  left: 10%;
  z-index: 1000;
  background-color: var(--White);
  color: var(--Black);
  width: 80vw;
  height: 80vh;
  margin: auto;
}

.header-text {
  font-size: 1rem;
  color: var(--Grey800);
}

.modal-body {
  padding: 1.25rem 3rem;
}

.close {
  cursor: pointer;
}

.search-physician {
  margin-right: 0;
}

.error {
  color: var(--ErrorRedDark)
}

.add-button-container {
  display: flex;
  justify-content: flex-end;
}
</style>
